/**
 * Shared helpers for building study-program dropdown labels, descriptions, and sort order.
 * Used by DocumentTemplateConfiguration and any other component that presents study programs in a dropdown.
 */

/**
 * Derives a department display name from a study-program object.
 * Tries `departmentName` first, then falls back to joining the `departments` array.
 *
 * @param {Object} studyProgram
 * @returns {string}
 */
export function buildStudyProgramDepartmentName(studyProgram) {
    if (studyProgram.departmentName) {
        return studyProgram.departmentName;
    }

    if (Array.isArray(studyProgram.departments) && studyProgram.departments.length > 0) {
        return studyProgram.departments
            .map(department => department && department.name)
            .filter(Boolean)
            .join(', ');
    }

    return '';
}

/**
 * Builds the human-readable label shown in the study-program dropdown.
 * Format: "<department> | <basicName> | <programName>"  (parts omitted when empty).
 *
 * @param {Object} studyProgram
 * @returns {string}
 */
export function buildStudyProgramDisplayName(studyProgram) {
    const departmentName = studyProgram.departmentName || buildStudyProgramDepartmentName(studyProgram);
    const programName = studyProgram.name || studyProgram.displayName || String(studyProgram.id);
    const basicName = studyProgram.studyProgramBasicName
        || (studyProgram.studyProgramBasic && studyProgram.studyProgramBasic.name)
        || '';

    const departmentAndBasic = [departmentName, basicName].filter(Boolean).join(' | ');

    if (departmentAndBasic) {
        return `${departmentAndBasic} | ${programName}`;
    }

    if (basicName) {
        return `${basicName} | ${programName}`;
    }

    return programName;
}

/**
 * Derives the cycle label shown as the dropdown item description.
 * Tries dedicated label properties first, then walks nested studyCycles arrays.
 *
 * @param {Object} studyProgram
 * @returns {string}
 */
export function buildStudyProgramCycleLabel(studyProgram) {
    if (studyProgram.cycleLabel) {
        return studyProgram.cycleLabel;
    }

    if (studyProgram.studyCycleLabel) {
        return studyProgram.studyCycleLabel;
    }

    const collectCycleLabels = (studyCycles) => studyCycles
        .map(studyCycleItem => {
            const cycle = (studyCycleItem && studyCycleItem.studyCycle) ? studyCycleItem.studyCycle : (studyCycleItem || {});
            if (cycle.displayName) return cycle.displayName;
            if (cycle.name) return cycle.name;
            if (cycle.number !== null && cycle.number !== undefined) return `${cycle.number}`;
            if (cycle._key) return cycle._key;
            return null;
        })
        .filter(label => !!label);

    const directStudyCycles = Array.isArray(studyProgram.studyCycles) ? studyProgram.studyCycles : [];
    const basicStudyCycles = studyProgram.studyProgramBasic && Array.isArray(studyProgram.studyProgramBasic.studyCycles)
        ? studyProgram.studyProgramBasic.studyCycles
        : [];

    const cycleLabels = [
        ...collectCycleLabels(directStudyCycles),
        ...collectCycleLabels(basicStudyCycles),
    ];

    if (cycleLabels.length === 0) {
        return '';
    }

    return Array.from(new Set(cycleLabels)).join(', ');
}

/**
 * Locale-aware string comparator used for alphabetical sorting of study-program names.
 *
 * @param {*} left
 * @param {*} right
 * @returns {number}
 */
export function compareByText(left, right) {
    const a = (left || '').toString().trim();
    const b = (right || '').toString().trim();
    return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
}

/**
 * Numeric comparator for cycleNumber – unknown / non-finite values sort last.
 *
 * @param {Object} left  study-program object
 * @param {Object} right study-program object
 * @returns {number}
 */
export function compareByCycleNumber(left, right) {
    const a = Number.isFinite(Number(left.cycleNumber)) ? Number(left.cycleNumber) : Number.MAX_SAFE_INTEGER;
    const b = Number.isFinite(Number(right.cycleNumber)) ? Number(right.cycleNumber) : Number.MAX_SAFE_INTEGER;
    return a - b;
}

/**
 * Sorts a study-program array in-place by:
 *   1. cycleNumber  (ascending)
 *   2. department name  (alphabetical)
 *   3. studyProgramBasic name  (alphabetical)
 *   4. own name / displayName  (alphabetical)
 *
 * Returns the same (now sorted) array for chaining convenience.
 *
 * @param {Object[]} studyPrograms  – raw API objects
 * @returns {Object[]}
 */
export function sortStudyPrograms(studyPrograms) {
    return [...studyPrograms].sort((a, b) => {
        const cycleCompare = compareByCycleNumber(a, b);
        if (cycleCompare !== 0) return cycleCompare;

        const departmentCompare = compareByText(
            a.departmentName || buildStudyProgramDepartmentName(a),
            b.departmentName || buildStudyProgramDepartmentName(b),
        );
        if (departmentCompare !== 0) return departmentCompare;

        const basicCompare = compareByText(
            a.studyProgramBasicName || (a.studyProgramBasic && a.studyProgramBasic.name),
            b.studyProgramBasicName || (b.studyProgramBasic && b.studyProgramBasic.name),
        );
        if (basicCompare !== 0) return basicCompare;

        return compareByText(
            a.name || a.displayName || String(a.id),
            b.name || b.displayName || String(b.id),
        );
    });
}




// WEBPACK FOOTER //
// ./src/views/DocumentTemplateConfiguration/studyProgramUtils.js